import React, { useEffect, useReducer, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { cleanRef, hasMinimumPlanType, i18nIntl, isNotEmpty } from '../../../lib/ep';
import { promoteSegment, removeSegment, deleteSegment, getSegments } from '../../../lib/actions.js';

import ActionLink from '../../builder/ActionLink';
import ActionButton, { CreateButton, SquaredButton } from '../../builder/ActionButton';
import ButtonCollection from '../../builder/ButtonCollection';
import ColorPicker from '../../builder/ColorPicker';
import ConfirmButton, { DeleteButton } from '../../builder/ConfirmButton';
import FloatingLayer from '../../builder/FloatingLayer';
import Input from '../../builder/Input';
import Message from '../../builder/Message';
import Model from '../../builder/Model';
import SaveButton from '../../builder/SaveButton';
import SectionSubtitle from '../../builder/SectionSubtitle';
import SectionTitle from '../../builder/SectionTitle';
import Select from '../../builder/Select';
import ToggleCollection from '../../builder/ToggleCollection';
import Tooltip from '../../builder/Tooltip';

import Module from '../../ui_items/Module';
import RibbonCollection from '../../ui_items/RibbonCollection';
import SectionHeader from '../../ui_items/SectionHeader';
import SimpleListOfItems from '../../ui_items/SimpleListOfItems';

import OrganizerSelector from '../../promotion/OrganizerSelector';

const maxHeight = 'calc(100dvh - 397px)';

const reducer = (state, action) => {
	switch(action.type) {
		case 'SET_SEGMENTS':
			return {
				...state,
				segments:action.segments,
			};

		case 'CHANGE_SEGMENT':
			if(action.k == 'title') state.segment.ref = cleanRef(action.v);

			return {
				...state,
				segment:{ ...state.segment, [action.k]:action.v },
				error:false,
			};

		case 'TOGGLE_ADD':
			return {
				...state,
				state:(state.state != 'select') ? 'select' : 'add',
				segment:{},
				brand_id:false,
				default_scope:action.scope,
				error:false,
			};
		
		case 'SELECT_BRAND':
			return {
				...state,
				brand_id:action.v,
			}
		
		case 'DELETE_SEGMENT':
			return {
				...state,
				segments: state.segments.filter(s=>s.Segment.id != action.id)
			}

		case 'EDIT_SEGMENT':
			let edit_segment = state.segments.find((s) => s.Segment.id == action.id)['Segment'];
			return {
				...state,
				state:'edit',
				segment:{...edit_segment},
			};

		case 'ERROR_CREATE':
			return {
				...state,
				error:action.error
			}

		case 'ADD_SEGMENT':
			let segments = state.segments.filter((s) => s.Segment.id != action.segment.Segment.id);
			segments = [...segments, action.segment];
			segments.sort((a, b) => ((a.Segment.id < b.Segment.id) ? -1 : 1));

			return {
				...state,
				segments:segments,
				state:'list',
			};

		default:
			return state;
	}
}

const SegmentForm = ({ segment, dispatch, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_edit = props?.is_edit ?? false;
	const brand_id = props?.brand_id ?? false;
	const has_brand = (brand_id && isNotEmpty(segment, 'organizing_brand')) ? true : false;
	const promotion_id = props?.promotion_id ?? false;
	const has_promotion = (promotion_id && isNotEmpty(segment, 'promotion_id')) ? true : false;

	const [ scope, setScope ] = useState(props?.default_scope ?? false);

	useEffect(() => {
		if(scope === false && props.brand_id != 'all') {
			if(has_promotion || promotion_id !== false) {
				setScope('promotion');
			} else if(has_brand || (props.brand_id !== false && props.brand_id != 'all')) {
				setScope('brand');
			} else {
				setScope('account');
			}
		}

		if(props.brand_id == 'all') {
			setScope('account');
		}
	}, []);

	const changeSegment = (k, v) => {
		dispatch({ type:'CHANGE_SEGMENT', k, v });
	};

	const changeScope = (k, v) => {
		setScope(v);
	};

	const promoteThis = (id, scope, cb) => {
		promoteSegment(has_error.segment_id, scope, () => {
			if(cb != undefined) cb();
			if(props.backButton != undefined) props.backButton();
		});
	};

	const createSegment = (cb) => {
		let data = { ...segment };
		if(scope == 'promotion') {
			data.promotion_id = promotion_id;
			if (brand_id) {
				data.organizing_brand = brand_id;
			}
		}
		if(scope == 'brand') {
			data.promotion_id = null;
			data.organizing_brand = brand_id;
		}
		if(scope == 'account') {
			data.promotion_id = null;
			data.organizing_brand = null;
		}

		$.post('/accounts/savesegment', data, (response) => {
			if (!response.status) {
				if(cb != undefined) cb();
				dispatch({ type:'ERROR_CREATE', error:response });
				return;
			}
			dispatch({ type:'ADD_SEGMENT', segment:response.segment });
			if(props.onCreate != undefined) props.onCreate(response.segment);
			if(cb != undefined) cb();
		}, 'JSON');
	};

	let scopes = [{ k:'account', v:"Global for this account" }];
	if(has_brand || (brand_id !== false && brand_id != 'all')) scopes.push({ k:'brand', v:"Exclusive for this organizing brand" });
	if(has_promotion || promotion_id !== false) scopes.push({ k:'promotion', v:"Exclusive for this promotion" });

	const has_error = props?.error ?? false;
	const has_promote = (has_error.segment_id != undefined && has_error.reason != 'global_segment') ? true : false;

	let error_msg = false;
	if(has_error !== false && segment.ref != undefined && segment.ref != "") {
		switch(has_error.reason) {
			case 'global_segment':
				error_msg = "An account segment with this reference already exists.";
				break;

			case 'brand_segment':
				error_msg = "A brand segment with this reference already exists.";
				break;

			case 'promotion_segment':
				error_msg = "A promotion segment with this reference already exists.";
				break;
		}

		if(error_msg !== false && has_error.reason != 'global_segment') {
			error_msg = <span>{i18n(error_msg)} <strong>{i18n("You can use another name as Reference to create this new segment, or promote the already created segment to a higher level.")}</strong></span>;
		}
	}

	const is_superadmin = window?.is_admin ?? false;

	// if undefined, this means that we're out of promotion environment (i.e. Organizing brands), so we can create
	const is_promo_creator = window.is_creator;

	if(!is_edit && is_superadmin && is_promo_creator != undefined && is_promo_creator === false) {
		return (
			<FloatingLayer _class={{size:'small'}} backButton={() => props.backButton()}>
				<div class="without_actions">
					<Message
						title="Superadmins can't create segments to other profiles."
						_class={{ other:'in_top in_bottom' }}
					/>
				</div>
			</FloatingLayer>
		)
	}

	return (
		<FloatingLayer _class={{size:'small'}} backButton={() => props.backButton()}>
			<div>
				<SectionTitle>{(is_edit) ? "Edit segment" : "Create a new segment"}</SectionTitle>

				{(scope == 'account') && (
					<SectionSubtitle>This segment will be available for all the promotions created within this account.</SectionSubtitle>
				)}

				{(scope == 'brand') && (
					<SectionSubtitle>This segment will be available for all the promotions created within this organizing brand.</SectionSubtitle>
				)}

				{(scope == 'promotion') && (
					<SectionSubtitle>This segment is only available for the promotion.</SectionSubtitle>
				)}

				<Model model={segment} change={changeSegment} errors={(error_msg !== false) ? { ref:error_msg } : null}>
					<Input name="title" _class={{size:60}}>Segment name</Input>

					<Input name="ref" _class={{size:60}}>Reference</Input>

					<ColorPicker name="color" _class={{size:60}} color={segment?.color || 'rgba(0, 0, 0, 1)'}>Color</ColorPicker>
				</Model>

				{(scopes.length > 1) && (
					<Select
						name="scope"
						_class={{size:60}}
						values={scopes}
						value={scope}
						change={changeScope}
					>Segment scope</Select>
				)}

				<ButtonCollection in_floating={true}>
					<SaveButton
						disabled={error_msg !== false}
						onClick={(error_msg === false) ? (e, cb) => createSegment(cb) : null}
					>{(is_edit) ? "Save changes" : "Create segment"}</SaveButton>

					{(has_promote) && (
						<ConfirmButton
							type='save'
							onClick={(e, cb) => promoteThis(has_error.segment_id, scope, cb)}
						>Promote segment</ConfirmButton>
					)}
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

export const SegmentsList = injectIntl(({ brands, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	if(brands == undefined) brands = [];

	const [ state, dispatch ] = useReducer(reducer, {
		state:'select',
		segments:false,
		segment:{},
		default_scope:'brand',
		brand_id:false,
	});

	const [ display_delete_confirm, setDisplayDeleteConfirm ] = useState(false);

	const has_min_plan = hasMinimumPlanType(5);

	if(!has_min_plan) {
		return (
			<SectionHeader
				title={i18n("User Segments")}
				subtitle={<>
					<p class="gui_not_in_bottom_small"><strong>{i18n("Plan required")}</strong></p>

					<p class="gui_not_in_bottom_small">{i18n("User segments will allow you to apply labels to promotion participants to group users and apply specific actions to the created segments, such as limiting a promotion to a specific group of users.")}</p>
					
					<p class="gui_not_in_bottom_small">
						<FormattedMessage
							id="Available to customers with an active {link}."
							values={{
								link:<ActionLink
									_href={`${window.panel_url}plans/add`}
									_target="_blank"
								>White Label Plan</ActionLink>,
							}}
						/>
					</p>
					
					<p>
						<ActionLink
							_href={`${window.zendesk_base_url}5848722065556`}
							_target='_blank'
						>Learn more about User Segments</ActionLink>.
					</p>
				</>}
				icon={{ icon:'knk-filter' }}
			>
				<ButtonCollection>
					<CreateButton
						_href={`${window.panel_url}plans/add`}
						_target="_blank"
					>Activate a plan</CreateButton>
				</ButtonCollection>
			</SectionHeader>
		)
	}

	const editSegment = (id) => {
		dispatch({ type:'EDIT_SEGMENT', id });
	};

	const confirmDeleteSegment = (id, confirm = false) => {
		setDisplayDeleteConfirm(false);

		deleteSegment(id, confirm, (res) => {
			if((res.status && (res.count == undefined || parseInt(res.count) < 1)) || confirm) {
				dispatch({ type:'DELETE_SEGMENT', id });
			} else {
				setDisplayDeleteConfirm({ id, total:parseInt(res.count) });
			}
		});
	};

	const closeEditSegment = () => {
		dispatch({ type:'TOGGLE_ADD' });
	};

	const onSelectBrand = (id) => {
		if(id != 'all' || true) {
			dispatch({ type:'SELECT_BRAND', v:id });
		}
	};

	const sortSegments = (segments_arr) => {
		return segments_arr.sort((a, b) => ((a.ref < b.ref) ? -1 : 1));
	};

	useEffect(() => {
		getSegments((response) => {
			dispatch({ type:'SET_SEGMENTS', segments:response });
		});
	}, []);

	let itemsToDisplay_promo = [];
	let itemsToDisplay_brand = [];
	let itemsToDisplay_account = [];

	if(state.segments.length > 0) {
		state.segments.map((_segment) => {
			const segment = _segment.Segment;

			const new_segment = {
				id:segment.id,
				title:<><strong>{segment.title}</strong> [{i18n("Internal reference")}: <strong>{segment.ref}</strong>]</>,
				ref:segment.title,
				is_html:true,
				autotranslate:false,
				ribbons:[{
					title:<>ID: #<strong>{segment.id}</strong></>,
					color:'gray',
					is_html:true,
					is_light:false,
				}],
				actions:<>
					<SquaredButton
						icon='knk-edit'
						onClick={() => editSegment(segment.id)}
						aria-label="Edit segment"
					/>

					<DeleteButton
						squared={true}
						onClick={() => confirmDeleteSegment(segment.id)}
						aria-label="Delete segment"
					/>
				</>,
			};

			if(segment.promotion_id != undefined && segment.promotion_id != '') {
				new_segment.ribbons.unshift({ title:<>{i18n("Promotion")}: #<strong>{segment.promotion_id}</strong></>, color:segment.color, is_html:true });

				itemsToDisplay_promo.push(new_segment);
			} else if(segment.organizing_brand != undefined && segment.organizing_brand != '') {
				let brand = brands.find((b) => (b.id == segment.organizing_brand));
				if(brand == undefined) brand = {};

				new_segment.type = 'brand';
				new_segment.ribbons.unshift({ title:<>{i18n("Organizing brand")}: <strong>{brand?.name ?? i18n("Unknown organizer")}</strong></>, color:segment.color, is_html:true })

				itemsToDisplay_brand.push(new_segment);
			} else {
				new_segment.type = 'account';
				new_segment.ribbons.unshift({ title:i18n("Global"), color:segment.color })

				itemsToDisplay_account.push(new_segment);
			}
		});
	}

	itemsToDisplay_account = sortSegments(itemsToDisplay_account);
	itemsToDisplay_brand = sortSegments(itemsToDisplay_brand);
	itemsToDisplay_promo = sortSegments(itemsToDisplay_promo);

	return (
		<>
			<SectionHeader
				title={i18n("User Segments")}
				subtitle={<>{i18n("User Segments allow you to apply labels to the participants of the promotions to make groups of users. You can create several user segments in order to group them according to the criteria you need. In this way, you will be able to apply specific actions to the user segments you create.")} <ActionLink _href={`${window.zendesk_base_url}5848722065556`} _target='_blank'>More information</ActionLink></>}
				total={<FormattedMessage id="{num} segments" values={{ num:state.segments.length }} />}
				icon={{ icon:'knk-filter' }}
			>
				<ButtonCollection>
					<CreateButton
						onClick={() => dispatch({ type:'TOGGLE_ADD' })}
					>Create a new segment</CreateButton>
				</ButtonCollection>
			</SectionHeader>

			<div class="gui_grid gui_overflowed_auto gui_not_padded_in_top">
				{(itemsToDisplay_account.length > 0) && (
					<Module title={{ label:"Global scope for the entire account" }}>
						<SimpleListOfItems
							items={itemsToDisplay_account}
							overflowed={false}
							{...props}
						/>
					</Module>
				)}

				{(itemsToDisplay_brand.length > 0) && (
					<Module title={{ label:"Exclusive scope for this organizing brand" }}>
						<SimpleListOfItems
							items={itemsToDisplay_brand}
							overflowed={false}
							{...props}
						/>
					</Module>
				)}

				{(itemsToDisplay_promo.length > 0) && (
					<Module title={{ label:"Exclusive scope for a specific promotion" }}>
						<SimpleListOfItems
							items={itemsToDisplay_promo}
							overflowed={false}
							{...props}
						/>
					</Module>
				)}
			</div>

			{(state.brand_id === false && state.state == 'add') && (
				<OrganizerSelector
					labels={brands}
					section_title="First, you need to select an organizing brand"
					enable_all={true}
					display_type='layer'
					onSelect={onSelectBrand}
					close={closeEditSegment}
					all_label="Globally scoped (all organizing brands)"
					select_all_cta_label="Make it global"
				/>
			)}

			{((state.brand_id !== false && state.state == 'add') || state.state == 'edit') && (
				<SegmentForm
					is_edit={(state.state == 'edit') ? true : false}
					brand_id={(state.state == 'add') ? state.brand_id : state.segment.organizing_brand}
					promotion_id={state.segment.promotion_id}
					segment={state.segment}
					error={state.error}
					default_scope={(state.segment?.promotion_id != undefined) ? 'promotion' : state.default_scope}
					dispatch={dispatch}
					backButton={closeEditSegment}
				/>
			)}

			{(display_delete_confirm !== false) && (
				<FloatingLayer
					_class={{ size:'small' }}
					enable_close={false}
				>
					<div>
						<SectionTitle _class={{icon:'knk-alert redIcon'}}>Warning</SectionTitle>

						<SectionSubtitle autotranslate={false}>
							<FormattedMessage
								id="This segment is currently assigned to {total} user(s)."
								values={{
									total:display_delete_confirm.total,
								}}
							/>
						</SectionSubtitle>

						<SectionSubtitle autotranslate={false}>
							<strong>{i18n("If you continue, this segment will be removed from all the users.")}</strong>
						</SectionSubtitle>

						<SectionSubtitle>Are you sure you want to continue?</SectionSubtitle>

						<ButtonCollection in_floating={true}>
							<ActionButton
								onClick={() => confirmDeleteSegment(display_delete_confirm.id, true)}
							>Continue</ActionButton>

							<ActionLink
								onClick={() => setDisplayDeleteConfirm(false)}
							>Cancel</ActionLink>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</>
	)
});

export const SegmentRibbons = ({ onDelete, ...props }) => {
	const segments = props?.segments ?? [];
	const blocked_segments = props?.blocked_segments ?? [];

	const [ all_segments, setAllSegments ] = useState(props?.all_segments ?? []);
	
	useEffect(() => {
		if(props.all_segments == undefined) {
			getSegments((response) => {
				setAllSegments(response);
			}, props.promotion_id);
		}
		if(props.all_segments != undefined) setAllSegments(props.all_segments);
	}, [ props.all_segments ]);
	
	if(segments == undefined || segments.length == 0 || all_segments === false) return null;

	const deleteSegmentFromUser = (segment_id) => {
		removeSegment(props.promotion_id, props.login_id, segment_id);
		if(onDelete != undefined) onDelete(segment_id);
	};

	const getSegmentRibbonProps = (segment, extra_props = null) => {
		if(typeof segment == 'object') return segment;
		const this_segment = all_segments.find((s) => { return s.Segment.id == segment });

		if(this_segment == undefined) return null;

		let segment_ribbon_props = {
			...extra_props,
			title:this_segment.Segment.title,
			color:this_segment.Segment.color,
		};

		if(onDelete != undefined) {
			segment_ribbon_props.onDelete = () => deleteSegmentFromUser(this_segment.Segment.id);
		}

		return segment_ribbon_props;
	};

	let segments_to_display = [];
	if(all_segments.length > 0 || segments != undefined && segments.length > 0) {
		if(segments.length > 0){
			segments.map((segment) => {
				segments_to_display.push(getSegmentRibbonProps(segment));
			});
		}
		if(blocked_segments.length > 0){
			blocked_segments.map((segment) => {
				segments_to_display.push(getSegmentRibbonProps(segment, { icon:'knk-blocked', tooltip:<Tooltip with_icon={false} position='top'>Blocked segment</Tooltip> }));
			});
		}
	}

	return (
		<RibbonCollection
			{...props}
			items={segments_to_display}
		/>
	)
};

export const SegmentPicker = ({ segmentSelected, ...props }) => {
	const [ segments, setSegments ] = useState(props.segments ?? false);

	useEffect(() => {
		if(props.segments == undefined) {
			getSegments((response) => {
				setSegments(response);
			},props.promotion_id);
		} else {
			setSegments(props.segments);
		}
	}, [ props.segments ]);

	const segments_to_toggle = [];
	if(segments !== false) {
		segments.map((s) => {
			segments_to_toggle.push(s.Segment);
		});
	}

	return (
		<ToggleCollection
			items={segments_to_toggle}
			onToggle={segmentSelected}
			selected={props.selected}
			filter_by={['ref', 'title']}
			first={true}
		/>
	)
};

const SegmentBuilder = (props) => {
	const enable_add = props?.enable_add ?? true;
	const display_picker = props?.display_picker ?? true;

	const [ state, dispatch ] = useReducer(reducer, {
		state:'select',
		segments:props?.all_segments ?? false,
		segment:{},
	});

	useEffect(() => {
		if(!state.segments) {
			getSegments((response) => {
				dispatch({ type:'SET_SEGMENTS', segments:response });
			}, props.promotion_id);
		}
	}, []);

	const segmentSelected = (segment_id) => {
		if(props.segmentSelected != undefined) props.segmentSelected(segment_id);
	};

	return (
		<>
			{(display_picker) && (
				<SegmentPicker
					segments={state.segments}
					segmentSelected={segmentSelected}
					selected={props.selected}
					promotion_id={props.promotion_id}
				/>
			)}

			{(enable_add) && (
				<ButtonCollection>
					<ActionButton
						color='green'
						_class={{ other:'addItemButton' }}
						onClick={() => dispatch({ type:'TOGGLE_ADD' })}
					>Create a new segment</ActionButton>
				</ButtonCollection>
			)}

			{(state.state == 'add') && (
				<SegmentForm
					segment={state.segment}
					dispatch={dispatch}
					onCreate={props.segmentCreated}
					brand_id={props.brand_id}
					error={state.error}
					promotion_id={props.promotion_id}
					backButton={() => dispatch({ type:'TOGGLE_ADD' })}
				/>
			)}
		</>
	)
};

export default injectIntl(SegmentBuilder);